import { bindActionCreators } from "@reduxjs/toolkit";
import { CampaignsFilterFormForPacks } from "components/filterForms/CampaignsFilterForm/CampaignsFilterFormForPacks";
import EmbeddedVideosFilterFormForPacks from "components/filterForms/EmbeddedVideosFilterForm/EmbeddedVideosFilterFormForPacks";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { batch, connect, type ConnectedProps } from "react-redux";
import { ContentView, type ContentTab } from "../../../../../components";
import AssessmentFilterFormForPacks from "../../../../../components/filterForms/AssessmentFilterForm/AssessmentFilterFormForPacks";
import { EmailFilterFormForPacks } from "../../../../../components/filterForms/EmailFilterForm/EmailFilterFormForPacks";
import { EventFilterFormForPacks } from "../../../../../components/filterForms/EventFilterForm/EventFilterFormForPacks";
import { FlowFilterFormForPacks } from "../../../../../components/filterForms/FlowFilterForm/FlowFilterFormForPacks";
import { MessageFilterFormForPacks } from "../../../../../components/filterForms/MessageFilterForm/MessageFilterFormForPacks";
import PdfsFilterFormForPacks from "../../../../../components/filterForms/PdfsFilterForm/PdfsFilterFormForPacks";
import SurveyFilterFormForPacks from "../../../../../components/filterForms/SurveyFilterForm/SurveyFilterFormForPacks";
import VideosFilterFormForPacks from "../../../../../components/filterForms/VideosFilterForm/VideosFilterFormForPacks";
import DragNDropItemTypes from "../../../../../enums/dragNDropItemTypes";
import { type AppDispatch, type RootState } from "../../../../Application/globaltypes/redux";
import { isPackReadOnlySelector } from "../../../../Application/selectors";
import PanelComponent from "../../../../Library/Flows/Designer/FlowComponentsPanel/contentView/PanelComponent/PanelComponent";
import { type EntityType } from "../../../../Library/Flows/Designer/types";
import { clearSelectedCards } from "../../state/actions/packDetailsActions";
import {
  activeViewTabSelector,
  itemsToDropFiltersSelector,
  itemsToDropSelector,
  packContentTypeSelectSelector,
} from "../../state/selectors";
import { setActiveTab, setEntityType } from "../../state/slices/packContentTabContextSlice";
import { reset as resetItemsAction } from "../../state/slices/packContentTabItemsToDropSlice";
import {
  resetAppliedFilter,
  resetFilter,
  setSearch as setSearchAction,
} from "../../state/slices/packsContentFiltersSlice";
import { fetchItemsToDropV2 } from "../../state/thunks/packContentTabItemsToDropThunk";
import { getContentViewTabsOptions } from "./contentViewOptions";
import PackContentFilterSidebarContainer from "./PackContentFilterSidebarContainer";
import "./packDrops.scss";
import { permissionsToAccountSelector } from "features/Accounts/EditAccount/state/slices/permissionsToAccountSlice";
import RolePermissions from "enums/rolePermissions";
import { fetchPermissionsToAccount } from "features/Accounts/EditAccount/state/thunks/permissionsToAccountThunk";
import LinksFilterFormForPacks from "components/filterForms/LinkFilterForm/LinksFilterFormForPacks";
import ScormFilterFormForPacks from "../../../../../components/filterForms/ScormFilterForm/ScormFilterFormForPacks";

export function PackDrops({
  changeViewTab,
  clearSelectedItems,
  dispose,
  activeViewTab,
  fetchPermissionsToAccount,
  accountId,
  ...props
}: PropsFromRedux) {
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    return () => {
      clearSelectedItems();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasThreatDefence = useMemo(
    () => props.accountOnEditPermissions.includes(RolePermissions.CampaignsManage),
    [props.accountOnEditPermissions],
  );

  const embeddedVideosEnabled = useFeatureFlag(FeatureFlags.EmbeddedVideos);
  const isTDAddonEnabled = useFeatureFlag(FeatureFlags.ThreatDefenceAddon);
  const linksEnabled = useFeatureFlag(FeatureFlags.LinkAssetType);
  const scormEnabled = useFeatureFlag(FeatureFlags.SCORM);

  useEffect(() => {
    fetchPermissionsToAccount(accountId);
  }, [fetchPermissionsToAccount, accountId]);

  const filterFormsMap: { [key in EntityType]?: ComponentType<any> } = {
    Video: VideosFilterFormForPacks,
    Survey: SurveyFilterFormForPacks,
    Assessment: AssessmentFilterFormForPacks,
    Email: EmailFilterFormForPacks,
    Message: MessageFilterFormForPacks,
    ExternalEvent: EventFilterFormForPacks,
    Flow: FlowFilterFormForPacks,
    Pdf: PdfsFilterFormForPacks,
    EmbeddedVideo: EmbeddedVideosFilterFormForPacks,
    Campaign: CampaignsFilterFormForPacks,
    Link: LinksFilterFormForPacks,
    SCORM: ScormFilterFormForPacks,
  };

  const contentViewTabs: ContentTab[] = useMemo(() => {
    const {
      isReadOnly,
      packContentFilter,
      loadItemsV2,
      resetAppliedFilters,
      itemsToDrop,
      search,
      entityTypeSelect,
      changeEntityType,
      setSearch,
      resetItems,
    } = props;

    return getContentViewTabsOptions(
      embeddedVideosEnabled,
      isTDAddonEnabled && hasThreatDefence,
      linksEnabled,
      scormEnabled,
    ).map((tab) => ({
      label: tab.label,
      id: tab.tabId,
      component: (
        <PanelComponent
          id={tab.filtersId}
          className="pack-components-section"
          key={tab.filtersId}
          permissions={tab.permissions}
          onFilterButtonClick={() => setShowFilter(true)}
          appliedFilters={packContentFilter.appliedFilter}
          resetAppliedFilter={resetAppliedFilters}
          renderCard={tab.renderCard(isReadOnly)}
          filterOptions={tab.filterOptions}
          loadItems={loadItemsV2}
          itemsToDrop={itemsToDrop}
          search={search}
          entityTypeSelect={entityTypeSelect}
          changeEntityType={(type) => {
            setShowFilter(false);
            clearSelectedItems();
            changeEntityType(type);
          }}
          setSearch={setSearch}
          resetItems={resetItems}
          dispose={dispose}
          dragNDropItemType={DragNDropItemTypes.PACK}
          showPurchased={false}
        />
      ),
    }));
  }, [
    props,
    dispose,
    clearSelectedItems,
    embeddedVideosEnabled,
    isTDAddonEnabled,
    hasThreatDefence,
    linksEnabled,
    scormEnabled,
  ]);

  return (
    <div className={"pack-drops"}>
      <ContentView
        tabs={contentViewTabs}
        activeIndex={activeViewTab}
        onTabChange={(_, r: { activeIndex: number }) => {
          changeViewTab(r.activeIndex);
          dispose();
          clearSelectedItems();
        }}
      />

      <PackContentFilterSidebarContainer
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterFormsMap={filterFormsMap}
      />
    </div>
  );
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  isReadOnly: isPackReadOnlySelector(state),
  packContentFilter: state.packs.packContentTab.filters,
  activeViewTab: activeViewTabSelector(state),
  accountId: state.userProfile.accountId,
  itemsToDrop: itemsToDropSelector(state),
  search: itemsToDropFiltersSelector(state).search,
  entityTypeSelect: packContentTypeSelectSelector(state),
  accountOnEditPermissions: permissionsToAccountSelector(state),
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    changeViewTab: bindActionCreators(setActiveTab, dispatch),
    resetAppliedFilters: bindActionCreators(resetAppliedFilter, dispatch),
    loadItemsV2: bindActionCreators(fetchItemsToDropV2, dispatch),
    changeEntityType: bindActionCreators(setEntityType, dispatch),
    setSearch: bindActionCreators(setSearchAction, dispatch),
    fetchPermissionsToAccount: bindActionCreators(fetchPermissionsToAccount, dispatch),
    resetItems: bindActionCreators(resetItemsAction, dispatch),
    clearSelectedItems: bindActionCreators(clearSelectedCards, dispatch),
    dispose: () =>
      batch(() => {
        dispatch(resetFilter());
        dispatch(resetItemsAction());
      }),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PackDrops);
export default ConnectedComponent;
